import type { Dayjs } from 'dayjs';

import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { convertUTCToLocal } from 'src/utils/format-time';
import { formatPhoneNumber } from 'src/utils/format-number';

import { truncateText } from 'src/pages/chat/utils/text-formatting';

import type { TableHeadCellProps } from '../components/head/table-head-custom';

// ----------------------------------------------------------------------
// Date Formatting
// ----------------------------------------------------------------------

export const formatTableDate = (value: any, isTimestamp: boolean = false) => {
  const updatedValue = isTimestamp ? value * 1000 : value;
  return convertUTCToLocal(updatedValue);
};

// ----------------------------------------------------------------------
// Phone Formatting
// ----------------------------------------------------------------------

export const formatTablePhone = (phone: string) => formatPhoneNumber(phone);

// ----------------------------------------------------------------------
// Text Truncation with Tooltip
// ----------------------------------------------------------------------

export const renderTruncatedText = (text: string, maxLength: number = 20) => {
  if (!text || text.length <= maxLength) return text;

  return (
    <Tooltip title={text}>
      <Typography component="span" sx={{ fontSize: 'inherit' }}>
        {truncateText(text, maxLength)}
      </Typography>
    </Tooltip>
  );
};

// ----------------------------------------------------------------------
// Column Visibility Helper
// ----------------------------------------------------------------------

export const getVisibleColumns = (
  columns: TableHeadCellProps[],
  visibleIds: string[]
): TableHeadCellProps[] => columns.filter((col) => visibleIds.includes(col.id));

// ----------------------------------------------------------------------
// Filter Application Logic
// ----------------------------------------------------------------------

export const applyColumnFilter = (data: any[], filters: { [key: string]: string }): any[] => {
  if (!filters || Object.keys(filters).length === 0) return data;

  return data.filter((row) =>
    Object.entries(filters).every(([key, value]) => {
      if (!value) return true;

      const rowValue = row[key];
      if (rowValue === null || rowValue === undefined) return false;

      // Handle different filter types
      if (typeof rowValue === 'string') {
        return rowValue.toLowerCase().includes(value.toLowerCase());
      }

      if (typeof rowValue === 'number') {
        return rowValue.toString().includes(value);
      }

      return false;
    })
  );
};

// ----------------------------------------------------------------------
// Date Range Filter
// ----------------------------------------------------------------------

export const applyDateRangeFilter = (
  data: any[],
  dateFilters: Array<{ id: string; fromDate: Dayjs; toDate: Dayjs }>
): any[] => {
  if (!dateFilters || dateFilters.length === 0) return data;

  return data.filter((row) =>
    dateFilters.every(({ id, fromDate, toDate }) => {
      if (!fromDate || !toDate) return true;

      const rowValue = row[id];
      if (!rowValue) return false;

      const rowDate = new Date(rowValue);
      const fromDateObj = typeof (fromDate as any)?.toDate === 'function' ? (fromDate as any).toDate() : new Date(fromDate as any);
      const toDateObj = typeof (toDate as any)?.toDate === 'function' ? (toDate as any).toDate() : new Date(toDate as any);
      return rowDate >= fromDateObj && rowDate <= toDateObj;
    })
  );
};

// ----------------------------------------------------------------------
// Check if Column has Active Filter
// ----------------------------------------------------------------------

export const isColumnFiltered = (
  columnId: string,
  filters: { [key: string]: string },
  dateFilters: Array<{ id: string; fromDate: Dayjs; toDate: Dayjs }>
): boolean => {
  // Check text/select filters
  const textFilter = filters[columnId];
  if (textFilter && String(textFilter).trim() !== '') {
    return true;
  }

  // Check date filters
  const dateFilter = dateFilters.find((f) => f.id === columnId);
  if (dateFilter && dateFilter.fromDate && dateFilter.toDate) {
    return true;
  }

  return false;
};

// ----------------------------------------------------------------------
// Get Locked Columns (columns that cannot be hidden)
// ----------------------------------------------------------------------

export const getLockedColumns = (columns: TableHeadCellProps[]): string[] =>
  columns.filter((col) => col.isDisable).map((col) => col.id);

// ----------------------------------------------------------------------
// Check if All Columns are Visible
// ----------------------------------------------------------------------

export const areAllColumnsVisible = (
  columns: TableHeadCellProps[],
  visibleColumns: string[],
  lockedColumns: string[]
): boolean => {
  const nonLockedColumns = columns.filter((col) => !lockedColumns.includes(col.id));
  const visibleNonLocked = visibleColumns.filter((id) => !lockedColumns.includes(id));

  return visibleNonLocked.length === nonLockedColumns.length;
};

// ----------------------------------------------------------------------
// Sync Visible Columns with Current Columns
// ----------------------------------------------------------------------

/**
 * Ensures visible columns array only contains valid column IDs from current columns
 * and adds any new columns that aren't in the visible list
 */
export const syncVisibleColumns = (
  currentColumns: TableHeadCellProps[],
  visibleColumns: string[]
): string[] => {
  const currentIds = currentColumns.map((col) => col.id);

  // Remove stale column IDs that no longer exist
  const validVisible = visibleColumns.filter((id) => currentIds.includes(id));

  // Add new columns that aren't in the visible list (default to visible)
  const newColumns = currentIds.filter((id) => !visibleColumns.includes(id));

  return [...validVisible, ...newColumns];
};

// ----------------------------------------------------------------------
// Clean Up Stale Column IDs
// ----------------------------------------------------------------------

/**
 * Removes column IDs from a list that don't exist in the current columns array
 */
export const cleanupStaleColumnIds = (
  currentColumns: TableHeadCellProps[],
  columnIds: string[]
): string[] => {
  const currentIds = new Set(currentColumns.map((col) => col.id));
  return columnIds.filter((id) => currentIds.has(id));
};

// ----------------------------------------------------------------------
// Validate Column Visibility State
// ----------------------------------------------------------------------

/**
 * Validates that the visible columns state is consistent with current columns
 * Returns an object with validation results and suggested fixes
 */
export const validateColumnVisibility = (
  currentColumns: TableHeadCellProps[],
  visibleColumns: string[],
  lockedColumns: string[]
): {
  isValid: boolean;
  issues: string[];
  suggestedVisible: string[];
} => {
  const issues: string[] = [];
  const currentIds = currentColumns.map((col) => col.id);
  const currentIdsSet = new Set(currentIds);

  // Check for stale column IDs in visible columns
  const staleVisible = visibleColumns.filter((id) => !currentIdsSet.has(id));
  if (staleVisible.length > 0) {
    issues.push(`Stale visible columns: ${staleVisible.join(', ')}`);
  }

  // Check for missing columns (columns that exist but aren't in visible list)
  const missingColumns = currentIds.filter((id) => !visibleColumns.includes(id));
  if (missingColumns.length > 0) {
    issues.push(`Missing columns: ${missingColumns.join(', ')}`);
  }

  // Check if any locked columns are hidden
  const hiddenLockedColumns = lockedColumns.filter((id) => !visibleColumns.includes(id));
  if (hiddenLockedColumns.length > 0) {
    issues.push(`Locked columns are hidden: ${hiddenLockedColumns.join(', ')}`);
  }

  // Generate suggested visible columns
  const validVisible = visibleColumns.filter((id) => currentIdsSet.has(id));
  const newColumns = currentIds.filter((id) => !visibleColumns.includes(id));
  const suggestedVisible = [...validVisible, ...newColumns, ...hiddenLockedColumns];

  return {
    isValid: issues.length === 0,
    issues,
    suggestedVisible: Array.from(new Set(suggestedVisible)), // Remove duplicates
  };
};
